// src/components/WebsitePage1.js
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Button, Alert, Spinner, Card, Form, Row, Col, Collapse, ProgressBar, Badge, Modal, Toast, ToastContainer } from 'react-bootstrap';
import useToken from '../../hooks/useToken';
import useProgressRedirect from '../../hooks/useProgressRedirect';
import ProgressTracker from '../shared/ProgressTracker';
import { Timer } from '../shared/Timer';
import ScopeFocusInstructions from '../shared/ScopeFocusInstructions';
import { EmbedPDF } from '@simplepdf/react-embed-pdf'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSitemap, faProjectDiagram } from '@fortawesome/free-solid-svg-icons';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const SEVERITY_STYLES = {
  Critical:      { bg: '#842029', text: '#fff' },
  High:          { bg: '#664d03', text: '#fff' },
  Medium:        { bg: '#084298', text: '#fff' },
  Low:           { bg: '#0f5132', text: '#fff' },
  Informational: { bg: '#495057', text: '#fff' },
};

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ['bold', 'italic', 'code'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['code-block'],
    ['clean'],
  ],
};

const isQuillEmpty = (val) =>
  !val || val === '<p><br></p>' || val.replace(/<[^>]*>/g, '').trim() === '';

/**
 * 
 * @param {object} props
 * @param {number} props.order_index 1, 2
 * @param {string} props.next_navigation /website2, /post-experiment
 * @param {string} props.route_name WEBSITE1, WEBSITE2
 * @param {string} props.next_route_name WEBSITE1, POST_EXPERIMENT
 * @returns 
 */
function WebsitePage({ order_index, route_name, next_route_name, next_navigation }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, refreshAccessToken, getValidToken } = useToken();

  const [website, setWebsite] = useState(null);
  const [dfdUrl, setDfdUrl] = useState(null);
  const [designDocUrl, setDesignDocUrl] = useState(null);
  const [serverIP, setServerIP] = useState('');
  const [extraServices, setExtraServices] = useState([]);
  const [testAccounts, setTestAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [vulnForms, setVulnForms] = useState([]);
  const [vulnTypes, setVulnTypes] = useState([]);
  const [completing, setCompleting] = useState(false);
  const [savingAll, setSavingAll] = useState(false);
  const [showEarlyExitModal, setShowEarlyExitModal] = useState(false);
  const [restarting, setRestarting] = useState(false);

  const [elapsed, setElapsed] = useState(null);
  const [mandatoryLimit, setMandatoryLimit] = useState(90 * 60);
  const [extraLimit, setExtraLimit] = useState(30 * 60);
  const [overMandatory, setOverMandatory] = useState(false);
  const [overExtra, setOverExtra] = useState(false);
  const [docReminder, setDocReminder] = useState(false);
  const reminderFiredRef = useRef(new Set());
  const [instructions, setInstructions] = useState(null);
  const [showInstructions, setShowInstructions] = useState(true);
  const isFirstTime = useRef(true);
  const isStartFirstTime = useRef(true);
  const timerStartedRef = useRef(false);

  const MAX_IMAGE_BYTES = 5 * 1024 * 1024;   // 5 MB

  useProgressRedirect(route_name); // <=== Auto redirect if necessary!

  useEffect(() => {
    const init = async () => {
      const authToken = await getValidToken();

      try {

        const assignRes = await fetch("/api"+`/assignment`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const assignments = await assignRes.json();

        // Get the website assignment with order_id
        const assignedWebsite = assignments.find(w => w.order_index === order_index || w.order_index === String(order_index));
        if (!assignedWebsite) {
          setError("You are not assigned to this website.");
          return;
        }
        setWebsite(assignedWebsite);
        console.log(assignedWebsite);

        // 🔽 Fetch the design document PDF
        const designDocRes = await fetch("/api"+`/website/${assignedWebsite.website_id}/design-document-pdf`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });

        if (designDocRes.ok) {
          const blob = await designDocRes.blob();
          setDesignDocUrl(URL.createObjectURL(blob));
        }


        // 🔽 Fetch testing instructions
        const instrRes = await fetch("/api"+`/website/${assignedWebsite.website_id}/testing-instructions`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (instrRes.ok) {
          setInstructions(await instrRes.json());
        }

        // 🔽 Fetch the threat model viewer
        const threatRes = await fetch("/api"+`/website/${assignedWebsite.website_id}/threat-model`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });

        if (threatRes.ok) {
          setDfdUrl("/threatmodelviewer");
        } else {
          console.warn("Threat model not available for this website.");
          setDfdUrl(null); // Set it to null to indicate it's not available
        }

        // Fetch vulnerability types
        const vulnTypeRes = await fetch("/api"+`/vulnerability-types`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const vulnTypeData = await vulnTypeRes.json();
        setVulnTypes(vulnTypeData);

        // Fetch saved findings
        const findingsRes = await fetch("/api"+`/website/${assignedWebsite.website_id}/findings`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (findingsRes.ok) {
          const savedFindings = await findingsRes.json();
          const formatted = savedFindings.map(f => ({
            impact: '',
            ...f,
            vulnerability_type: (() => {
              if (typeof f.vulnerability_type === 'string') {
                try {
                  const parsed = JSON.parse(f.vulnerability_type);
                  return Array.isArray(parsed) ? parsed : [f.vulnerability_type];
                } catch (e) {
                  return f.vulnerability_type ? [f.vulnerability_type] : [];
                }
              }
              return Array.isArray(f.vulnerability_type) ? f.vulnerability_type : [];
            })(),
            isNew: false,
          }));
          setVulnForms(formatted);
        }
      } catch (err) {
        console.error(err);
        setError('Failed to load assignment.');
      }
    };
    init();

    // return () => clearInterval(timerRef.current); // cleanup timer when page unmounts

  }, [id]);

  /* ───────────────────── helpers (auth + upload) ────────────────── */
  const withAuth = async (opts = {}) => {
    const authToken = await getValidToken();
    return {
      ...opts,
      headers: {
        ...(opts.headers || {}),
        Authorization: `Bearer ${authToken}`,
      },
    };
  };

  /** upload file → `/upload/poc-image` → return CDN url */
  const uploadScreenshot = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("website_id", website.website_id);
    const res = await fetch("/api/upload/poc-image", await withAuth({ method: "POST", body: fd }));
    if (!res.ok) throw new Error("Image upload failed");
    const { url } = await res.json();
    return url;
  };

  /** called when user picks a file for one finding */
  const handleFileChange = async (idx, file) => {
    if (!file) return;

    if (file.size > MAX_IMAGE_BYTES) {
      alert("Screenshot must be ≤ 5 MB");
      return;
    }

    setVulnForms(fs => fs.map((f, i) => i === idx ? { ...f, uploading: true } : f));
    try {
      const url = await uploadScreenshot(file);
      setVulnForms(fs => fs.map((f, i) => i === idx ? { ...f, poc_image_path: url, uploading: false } : f));
    } catch (e) {
      alert(e.message);
      setVulnForms(fs => fs.map((f, i) => i === idx ? { ...f, uploading: false } : f));
    }
  };

  const handleStartServer = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch("/api"+`/website/${website.website_id}/start`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${await getValidToken()}` },
      });
      if (!res.ok && res.headers.get('content-type')?.includes('text/html')) {
        setError(`Server gateway error (HTTP ${res.status}). The environment may still be starting — please wait 30 seconds and refresh.`);
        return;
      }
      const data = await res.json();
      if (res.ok && data.hostname) {
        setServerIP(data.hostname);
        setExtraServices(data.extra_services || []);
        setTestAccounts(data.test_accounts || []);

        // ✅ Re-fetch the assignment data to get the updated `server_started_at`
        const authToken = await getValidToken();

        try {

          const assignRes = await fetch("/api"+`/assignment`, {
            headers: { Authorization: `Bearer ${authToken}` },
          });
          
          if (!assignRes.ok) {
            console.error('Failed to fetch assignment:', assignRes.status);
            return;
          }
          
          const assignments = await assignRes.json();

          // Get the website assignment with order_id 1
          const assignedWebsite = assignments.find(w => w.order_index === order_index);
          if (!assignedWebsite) {
            setError("You are not assigned to this website.");
            return;
          }
          setWebsite(assignedWebsite);
          if (!timerStartedRef.current) {
            timerStartedRef.current = true;
            setElapsed(0);
          }
        } catch (err) {
          console.error(err);
          setError('Failed to load assignment.');
        }
      } else {
        setError(data.message || 'Failed to start server.');
      }
    } catch (err) {
      setError('Server startup error.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!serverIP && website && isStartFirstTime.current) {
      isStartFirstTime.current = false;
      handleStartServer();
    }
  })

  const handleRestart = async () => {
    setLoading(true);
    setRestarting(true);
    setError('');
    try {
      const res = await fetch("/api"+`/website/${website.website_id}/restart`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${await getValidToken()}` },
      });
      const data = await res.json();
      if (res.ok && data.hostname) {
        setServerIP(data.hostname);
        setExtraServices(data.extra_services || []);
        setTestAccounts(data.test_accounts || []);
      } else {
        setError(data.message || 'Failed to restart server.');
      }
    } catch (err) {
      setError('Server restart error.');
    } finally {
      setLoading(false);
      setRestarting(false);
    }
  };

  const handleStop = async () => {
    if (!website) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch("/api"+`/website/${website.website_id}/stop`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${await getValidToken()}` },
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.message || 'Failed to stop server.');
      } else {
        setServerIP('');
        setExtraServices([]);
      }
    } catch (err) {
      setError('Server stop error.');
    } finally {
      setLoading(false);
    }
  };


  const handleAddFinding = () => {
    if (vulnForms.length >= 20) return;
    setVulnForms([...vulnForms, { id: null, vulnerability_type: [], name: '', severity: '', affected_urls: '', description: '', payload: '', impact: '', poc_image_path: '', used_threat_model: false, threat_model_info: '', used_design_document: false, design_document_info: '', isNew: true, collapsed: false }]);
  };

  const handleRemoveFinding = async (index) => {
    const finding = vulnForms[index];
  
    if (finding.id) {
      // If it has an ID, it's saved in the backend, delete it first
      const confirmDelete = window.confirm("This finding is already saved. Do you really want to delete it?");
      if (!confirmDelete) return;
  
      try {
        const res = await fetch("/api"+`/website/${website.website_id}/finding/${finding.id}`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${await getValidToken()}`,
          },
        });
        if (!res.ok) {
          const data = await res.json();
          alert(data.message || "Failed to delete finding on server.");
          return;
        }
      } catch (err) {
        console.error('Failed to delete finding:', err);
        alert("Error deleting finding.");
        return;
      }
    }
  
    // Remove from UI
    const updated = [...vulnForms];
    updated.splice(index, 1);
    setVulnForms(updated);
  };
  
  const toggleCollapse = (index) => {
    const updated = [...vulnForms];
    updated[index].collapsed = !updated[index].collapsed;
    setVulnForms(updated);
  };

  const handleSaveFinding = async (index) => {
    const finding = vulnForms[index];
    if (!finding.vulnerability_type || finding.vulnerability_type.length === 0) return alert("Vulnerability Type is required.");
    if (!finding.name?.trim()) return alert("Title is required.");
    if (!finding.severity) return alert("Severity is required.");
    if (!finding.affected_urls?.trim()) return alert("Affected Endpoint is required.");
    if (!finding.description?.trim()) return alert("Description is required.");
    if (isQuillEmpty(finding.payload)) return alert("Steps to Reproduce is required.");
    if (!finding.impact?.trim()) return alert("Impact is required.");

    const url = `/website/${website.website_id}/finding${finding.id ? `/${finding.id}` : ''}`;
    const method = finding.id ? 'PUT' : 'POST';

    const payload = {
      ...finding,
      vulnerability_type: JSON.stringify(finding.vulnerability_type)
    };
    const res = await fetch("/api"+url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await getValidToken()}`,
      },
      body: JSON.stringify(payload),
    });
    const result = await res.json();
    if (res.ok) {
      const updated = [...vulnForms];
      updated[index].isNew = false;
      if (!finding.id && result.finding_id) updated[index].id = result.finding_id;
      setVulnForms(updated);
    } else {
      alert(result.message || 'Failed to save finding');
    }
  };

  const handleFindingChange = (index, field, value) => {
    const updated = [...vulnForms];
    updated[index][field] = value;
    setVulnForms(updated);
  };

  const handleVulnTypeToggle = (findingIndex, vulnType) => {
    const updatedForms = [...vulnForms];
    const finding = updatedForms[findingIndex];
    const currentTypes = finding.vulnerability_type || [];

    if (currentTypes.includes(vulnType)) {
      finding.vulnerability_type = currentTypes.filter(vt => vt !== vulnType);
    } else {
      finding.vulnerability_type = [...currentTypes, vulnType];
    }
    setVulnForms(updatedForms);
  };

  // put this just above the return statement, beside the other handlers
  const saveAllFindings = React.useCallback(async () => {
    if (!website) return;

    // make a *fresh* token only once
    const authToken = await getValidToken();

    // Kick off one request per finding (skip blanks)
    const savePromises = vulnForms
      .filter(f => f.vulnerability_type && f.vulnerability_type.length > 0)        // ignore empty rows
      .map(async f => {
        const url = `/website/${website.website_id}/finding${f.id ? `/${f.id}` : ''}`;
        const method = f.id ? 'PUT' : 'POST';
        const payload = {
          ...f,
          vulnerability_type: JSON.stringify(f.vulnerability_type)
        };
        const res = await fetch("/api"+url, {
          method,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          // surface one error and let the rest continue
          const data = await res.json();
          throw new Error(data.message || 'Save failed');
        }
        return res.json();     // not strictly needed, but nice if you care
      });

    // Wait for all of them (fail fast on *any* error)
    await Promise.all(savePromises);

    // Mark everything clean in the UI
    setVulnForms(fs => fs.map(f => ({ ...f, isNew: false, collapsed: false })));
  }, [refreshAccessToken, token, vulnForms, website]);

  const handleFinish = async () => {
    if (!overMandatory) {
      setShowEarlyExitModal(true);
      return;
    }
    setCompleting(true);
    try {
      await saveAllFindings();           // 🆕 bulk save first
      if (serverIP) await handleStop();  // stop the server only after saving

      const authToken = await getValidToken();
      await fetch("/api"+'/player/progress', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ progress_step: next_route_name }),
      });

      navigate(next_navigation, { replace: true });
      window.location.reload();
    } catch (err) {
      console.error(err);
      alert(err.message ?? 'Failed to complete - some findings may not be saved.');
    } finally {
      setCompleting(false);
    }
  };

  useEffect(() => {
    if (!website) return;
    if (!isFirstTime.current) return;
    isFirstTime.current = false;
    const mLimit = (website.time_limit_minutes || 90) * 60;
    const eLimit = (website.time_limit_extra_minutes || 30) * 60;
    setMandatoryLimit(mLimit);
    setExtraLimit(eLimit);
    if (website.server_started_at) {
      const parsed = Date.parse(website.server_started_at);
      if (!isNaN(parsed)) {
        timerStartedRef.current = true;
        const diff = Math.floor((new Date() - new Date(parsed)) / 1000);
        const elapsedSecs = Math.max(0, diff);
        setElapsed(elapsedSecs);
        if (elapsedSecs >= mLimit) setOverMandatory(true);
        if (elapsedSecs >= mLimit + eLimit) setOverExtra(true);
      }
    }
    // No server_started_at: elapsed stays null, timer stays hidden until first start
  }, [website]);

  // Soft document reminder toasts at 15, 45, and 75 minutes elapsed
  const DOC_REMINDER_TRIGGERS = [1 * 60, 15 * 60, 45 * 60, 75 * 60];
  useEffect(() => {
    if (elapsed === null) return;
    for (const trigger of DOC_REMINDER_TRIGGERS) {
      if (elapsed >= trigger && !reminderFiredRef.current.has(trigger)) {
        reminderFiredRef.current.add(trigger);
        setDocReminder(true);
        break;
      }
    }
  }, [elapsed]);

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">{error}</Alert>
        <Button variant="secondary" onClick={() => navigate('/dashboard', { replace: true })}>Back to Dashboard</Button>
      </Container>
    );
  }

  if (!website) return <Container className="py-5"><Spinner animation="border" /></Container>;

  return (
    <Container className="py-5">
      {
        // Website2 is disabled — only show Website1 or jump to Post-Experiment
        order_index === 1 ? (
          <ProgressTracker currentStep="website1" />
        ) : (
          <ProgressTracker currentStep="post_experiment" />
        )
      }
      <Card className="shadow-lg border-0 mb-4">
        <Card.Body>
          <h2 className="mb-3">🔎 {website.website_name}</h2>
          <p className="text-muted">{website.description}</p>

          {elapsed !== null && (
            <>
              <Timer
                initialElapsed={elapsed}
                limit={mandatoryLimit}
                onLimitReached={() => setOverMandatory(true)}
                extraLimit={mandatoryLimit + extraLimit}
                onExtraLimitReached={() => setOverExtra(true)}
                showHours
              />
              {overExtra && (
                <div className="mb-3 p-3 rounded" style={{ background: '#f8d7da', border: '1px solid #f5c2c7' }}>
                  <p className="mb-0 fw-bold" style={{ color: '#842029', fontSize: '1rem' }}>
                    ⚠️ You have spent an extra {Math.round(extraLimit / 60)} minutes. We strongly advise you to move on to the next step.
                  </p>
                </div>
              )}
              {overMandatory && !overExtra && (
                <div className="mb-3 p-3 rounded" style={{ background: '#fff3cd', border: '1px solid #ffc107' }}>
                  <p className="mb-0 fw-bold" style={{ color: '#856404', fontSize: '1rem' }}>
                    ⏰ Mandatory testing time has passed. Feel free to proceed to the next step whenever you are ready, or keep testing.
                  </p>
                </div>
              )}
            </>
          )}

          {instructions && (
            <ScopeFocusInstructions
              instructions={instructions}
              defaultOpen={showInstructions}
            />
          )}

          {serverIP ? (
            <>
              {restarting ? (
                <Alert variant="warning" className="d-flex align-items-center gap-2">
                  <Spinner animation="border" size="sm" />
                  <span>
                    <strong>Restarting…</strong> The server is being restarted and reset to a
                    clean state. This may take <strong>several minutes</strong>. Please wait.
                  </span>
                </Alert>
              ) : (
                <>
                  <Alert variant="success">
                    ✅ Server started! Access it at:{' '}
                    <a href={`http://${serverIP}`} target="_blank" rel="noopener noreferrer">
                      <strong>http://{serverIP}</strong>
                    </a>
                  </Alert>
                  {extraServices.length > 0 && (
                    <Alert variant="info" className="py-2">
                      <strong>Additional services:</strong>
                      <ul className="mb-0 mt-1">
                        {extraServices.map(s => (
                          <li key={s.url}>
                            {s.label}:{' '}
                            <a href={s.url} target="_blank" rel="noopener noreferrer" className="alert-link">{s.url}</a>
                          </li>
                        ))}
                      </ul>
                      <small className="text-muted d-block mt-2">
                        <em>Note: These are simulation services used by the application. You do not need to pentest them.</em>
                      </small>
                    </Alert>
                  )}
                  {testAccounts.length > 0 && (
                    <Alert variant="secondary" className="py-2">
                      <strong>🔑 Test Accounts</strong>
                      <table className="table table-sm table-bordered mb-0 mt-2 bg-white">
                        <thead className="table-light">
                          <tr>
                            <th>Role</th>
                            <th>Username</th>
                            <th>Password</th>
                          </tr>
                        </thead>
                        <tbody>
                          {testAccounts.map((acc, i) => (
                            <tr key={i}>
                              <td><Badge bg="secondary">{acc.role}</Badge></td>
                              <td><code>{acc.username}</code></td>
                              <td><code>{acc.password}</code></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Alert>
                  )}
                </>
              )}
              <div className="d-flex gap-2 mb-3">
                <Button variant="warning" onClick={handleRestart} disabled={loading}>🔄 {loading ? 'Restarting...' : 'Restart Server'}</Button>
                <Button variant="danger" onClick={handleStop} disabled={loading}>🛑 {loading ? 'Stopping...' : 'Stop Server'}</Button>
              </div>
            </>
          ) : (
            <>
              {loading && (
                <Alert variant="warning" className="d-flex align-items-center gap-2">
                  <Spinner animation="border" size="sm" />
                  <span>
                    <strong>Starting up…</strong> The target environment is being initialised.
                    This may take <strong>up to 3 minutes</strong> on first launch. Please wait.
                  </span>
                </Alert>
              )}
              <div className="d-grid mb-3">
                <Button variant="primary" onClick={handleStartServer} disabled={loading}>
                  {loading ? <><Spinner animation="border" size="sm" className="me-2" />Starting Server…</> : '🚀 Start Pentest Server'}
                </Button>
              </div>
            </>
          )}
        </Card.Body>
      </Card>
      
      <Card className="mb-4 shadow-sm">
        <Card.Body>
          <h5 className="mb-3">📄 Project Documents</h5>
          <div className="row">
          {designDocUrl && (
              <div className={dfdUrl ? "col-md-6" : "col-md-12"}>
                <Button href={`/document-view/${website.website_id}`} target="_blank" rel="noopener noreferrer" variant="primary" className="w-100 mb-2 mb-md-0">
                  <FontAwesomeIcon icon={faSitemap} /> Open Design Document Viewer
                  <span style={{ display: 'block', fontSize: '0.72em', opacity: 0.85 }}>↗ opens in a new tab</span>
                </Button>
              </div>
          )}
          {dfdUrl && (
              <div className="col-md-6">
                  <Button href={dfdUrl} target="_blank" rel="noopener noreferrer" variant="success" className="w-100">
                    <FontAwesomeIcon icon={faProjectDiagram} /> Open Threat Model Viewer
                    <span style={{ display: 'block', fontSize: '0.72em', opacity: 0.85 }}>↗ opens in a new tab</span>
                  </Button>
              </div>
          )}
          </div>

        </Card.Body>
      </Card>

      <Card className="shadow-sm">
        <Card.Body>
          <h4 className="mb-3">📝 Submit Findings</h4>
          {vulnForms.map((finding, index) => (
            <Card key={index} className="mb-3 shadow-sm">
              <Card.Header
                className="d-flex justify-content-between align-items-center"
                onClick={() => toggleCollapse(index)}
                style={{ cursor: 'pointer' }}
              >
                <strong>{finding.name || (Array.isArray(finding.vulnerability_type) && finding.vulnerability_type.join(', ')) || `Finding #${index + 1}`}</strong>
                <Button size="sm" variant="light">
                  {finding.collapsed ? "▶" : "▼"}
                </Button>
              </Card.Header>

              <Collapse in={!finding.collapsed}>
                <div>
                  <Card.Body>
                    {/* Place your existing <Form> content here */}
                    <Form key={index} className="border rounded p-3 mb-3">
                      <Alert variant="danger" className="py-2 mb-3">
                        <small>Fields marked with <i className="text-danger">*</i> are mandatory. All other fields are optional.</small>
                      </Alert>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-2">
                            <Form.Label>Vulnerability Type <i className="text-danger">*</i></Form.Label>
                            <div>
                              {vulnTypes.map(vt => (
                                <Badge
                                  key={vt}
                                  pill
                                  bg={finding.vulnerability_type?.includes(vt) ? 'primary' : 'secondary'}
                                  onClick={() => handleVulnTypeToggle(index, vt)}
                                  style={{ cursor: 'pointer', marginRight: '5px', marginBottom: '5px' }}
                                  className="p-2"
                                >
                                  {vt}
                                </Badge>
                              ))}
                            </div>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-2">
                            <Form.Label>Title <i className="text-danger">*</i></Form.Label>
                            <Form.Control
                              type="text"
                              value={finding.name}
                              onChange={(e) => handleFindingChange(index, 'name', e.target.value)}
                              placeholder="e.g. Unauthenticated Admin Access via IDOR"
                              required
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={4}>
                          <Form.Group className="mb-2">
                            <Form.Label>Severity <i className="text-danger">*</i></Form.Label>
                            <Form.Select
                              value={finding.severity || ''}
                              onChange={(e) => handleFindingChange(index, 'severity', e.target.value)}
                              required
                              style={
                                finding.severity && SEVERITY_STYLES[finding.severity]
                                  ? {
                                      backgroundColor: SEVERITY_STYLES[finding.severity].bg,
                                      color: SEVERITY_STYLES[finding.severity].text,
                                      fontWeight: 'bold',
                                      border: 'none',
                                    }
                                  : {}
                              }
                            >
                              <option value="">-- Select --</option>
                              <option value="Critical">Critical</option>
                              <option value="High">High</option>
                              <option value="Medium">Medium</option>
                              <option value="Low">Low</option>
                              <option value="Informational">Informational</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={8}>
                          <Form.Group className="mb-2">
                            <Form.Label>Affected Endpoint <i className="text-danger">*</i></Form.Label>
                            <Form.Control
                              type="text"
                              value={finding.affected_urls}
                              onChange={(e) => handleFindingChange(index, 'affected_urls', e.target.value)}
                              required
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <Form.Group className="mb-2">
                        <Form.Label>Description <i className="text-danger">*</i></Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={finding.description}
                          onChange={(e) => handleFindingChange(index, 'description', e.target.value)}
                          placeholder="Briefly describe the vulnerability and how it occurs."
                          required
                        />
                      </Form.Group>
                      <Form.Group className="mb-2">
                        <Form.Label>Steps to Reproduce <i className="text-danger">*</i></Form.Label>
                        <ReactQuill
                          theme="snow"
                          value={finding.payload}
                          onChange={(val) => handleFindingChange(index, 'payload', val)}
                          modules={QUILL_MODULES}
                          style={{ minHeight: '280px' }}
                          placeholder="1. Navigate to..."
                        />
                      </Form.Group>
                      <Form.Group className="mb-2 mt-5">
                        <Form.Label>Impact <i className="text-danger">*</i></Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={finding.impact || ''}
                          onChange={(e) => handleFindingChange(index, 'impact', e.target.value)}
                          placeholder="Describe the potential business or security impact of this vulnerability."
                          required
                        />
                      </Form.Group>
                      <Form.Group className="mb-2">
                        <Form.Label>PoC Screenshot (Max. 5MB)</Form.Label>
                        {finding.poc_image_path && (
                          <div className="mb-2">
                            <img src={finding.poc_image_path} alt="preview" className="img-thumbnail" style={{maxWidth:160}} />
                          </div>
                        )}
                        <Form.Control
                          type="file"
                          accept="image/*"
                          disabled={finding.uploading}
                          onChange={e=>handleFileChange(index, e.target.files?.[0])}
                        />
                        {finding.uploading && <ProgressBar now={100} animated striped className="mt-2" />}
                      </Form.Group>
                      <div className="mb-2 p-3 rounded" style={{ background: '#f8f9fa' }}>
                        {dfdUrl && (
                          <>
                            <Form.Check
                              type="checkbox"
                              id={`tm-check-${index}`}
                              label="Did the Threat Model information help you find this vulnerability?"
                              className="mb-2"
                              checked={finding.used_threat_model}
                              onChange={(e) => handleFindingChange(index, 'used_threat_model', e.target.checked)}
                            />
                            {finding.used_threat_model && (
                              <Form.Group className="mb-2 ms-4">
                                <Form.Label className="text-muted small">How did the Threat Model help? (optional)</Form.Label>
                                <Form.Control
                                  as="textarea"
                                  rows={2}
                                  value={finding.threat_model_info}
                                  onChange={(e) => handleFindingChange(index, 'threat_model_info', e.target.value)}
                                />
                              </Form.Group>
                            )}
                          </>
                        )}
                        <Form.Check
                          type="checkbox"
                          id={`dd-check-${index}`}
                          label="Did the Design Document information help you find this vulnerability?"
                          className="mb-1"
                          checked={finding.used_design_document}
                          onChange={(e) => handleFindingChange(index, 'used_design_document', e.target.checked)}
                        />
                        {finding.used_design_document && (
                          <Form.Group className="ms-4">
                            <Form.Label className="text-muted small">How did the Design Document help? (optional)</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={2}
                              value={finding.design_document_info}
                              onChange={(e) => handleFindingChange(index, 'design_document_info', e.target.value)}
                            />
                          </Form.Group>
                        )}
                      </div>
                      <div className="d-flex justify-content-between mt-3">
                        <Button variant="danger" onClick={()=>handleRemoveFinding(index)}>❌ Remove</Button>
                        <Button
                          variant="success"
                          disabled={finding.uploading}
                          onClick={()=>handleSaveFinding(index)}
                        >
                          {finding.isNew ? "💾 Add" : "💾 Save"}
                          </Button>
                      </div>
                    </Form>
                  </Card.Body>
                </div>
              </Collapse>
            </Card>
            
          ))}
          {vulnForms.length < 20 && (
            <div className="d-grid">
              <Button variant="outline-primary" onClick={handleAddFinding}>➕ Add New Finding</Button>
            </div>
          )}
        </Card.Body>
      </Card>
      <div className="d-flex gap-2 my-4">
        <Button
          variant="outline-primary"
          disabled={loading || completing || savingAll}
          onClick={async () => {
            setSavingAll(true);
            try {


              await saveAllFindings();        // bulk save
              // optional toast/snackbar here
            } catch (err) {
              console.error(err);
              alert(err.message ?? 'Bulk save failed.');
            } finally {
              setSavingAll(false);
            }
          }}
        >
          {savingAll ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Saving…
            </>
          ) : (
            '💾 Save All Findings'
          )}
        </Button>
        <Button
          variant="success"
          disabled={loading || completing}
          onClick={handleFinish}
        >
          {completing ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Completing…
            </>
          ) : (
            '✅ Complete this Website'
          )}
        </Button>

        {/* TODO: debug-only, remove before real experiment runs */}
        <Button
          variant="warning"
          onClick={async () => {
            const authToken = await getValidToken();
            await fetch("/api" + '/player/progress', {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
              body: JSON.stringify({ progress_step: next_route_name }),
            });
            navigate(next_navigation, { replace: true });
          }}
        >
          ⚠️ Skip (Testing)
        </Button>

        </div>

      <ToastContainer position="bottom-end" className="p-3" style={{ zIndex: 1100 }}>
        <Toast
          show={docReminder}
          onClose={() => setDocReminder(false)}
          autohide
          delay={8000}
          bg="light"
        >
          <Toast.Header closeButton>
            <span className="me-2">💡</span>
            <strong className="me-auto" style={{ fontWeight: 600 }}>Reminder</strong>
          </Toast.Header>
          <Toast.Body style={{ color: '#444', fontSize: '0.9rem' }}>
            Design documents are available in the panel above if you'd like to refer to them.
          </Toast.Body>
        </Toast>
      </ToastContainer>

      <Modal show={showEarlyExitModal} onHide={() => setShowEarlyExitModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>⏳ Testing period not complete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>You must complete the full <strong>{Math.round(mandatoryLimit / 60)}-minute</strong> mandatory testing period before proceeding.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowEarlyExitModal(false)}>OK</Button>
        </Modal.Footer>
      </Modal>

    </Container>
  );
}

export default WebsitePage;
